import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { FooterComponent } from 'src/app/layouts/footer/footer.component';
import { AuthService } from 'src/app/services/auth.service';
import { LoaderService } from 'src/app/services/loader.service';
import { interval, Subscription } from 'rxjs';
import { takeWhile } from 'rxjs/operators';

@Component({
  selector: 'app-email-verification',
  templateUrl: './email-verification.component.html',
  styles: [],
  standalone: true,
  imports: [CommonModule, FooterComponent, ReactiveFormsModule]
})
export class EmailVerificationComponent implements OnInit {

  private readonly auth = inject(AuthService);
  private readonly _toastrService = inject(ToastrService);
  private readonly router = inject(Router);
  private readonly _loaderService: LoaderService = inject(LoaderService);
  private readonly fb = inject(FormBuilder);
  
  public isLoading = this._loaderService.isLoading;
  public countdown = signal(0);
  public canResend = signal(true);
  public verificationForm!: FormGroup;
  public step = signal(2); // 1: solicitar código, 2: verificar código
  private timerSubscription: Subscription | null = null;
  
  ngOnInit(): void {
    // Verificar si el usuario está logueado
    if (!this.auth.isLoggedIn()) {
      this.router.navigateByUrl('/login');
      return;
    }
    
    // Si el usuario ya tiene su correo verificado, redireccionar
    // Esta validación se debe agregar a la API en el backend
    // Por ahora asumimos que el usuario no tiene su correo verificado
    
    // Inicializar el formulario
    this.initForm();
  }
  
  private initForm(): void {
    this.verificationForm = this.fb.group({
      verificationCode: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(8)]]
    });
  }

  logout(): void {
    this.auth.logout();
  }

  resendVerificationEmail(): void {
    if (!this.canResend()) return;
    
    const userName = this.auth.getUserEmail();
    if (!userName) {
      this._toastrService.error('No se pudo obtener la información del usuario.');
      return;
    }

    this.canResend.set(false);
    this.countdown.set(20); // 20 segundos de espera
    
    this.auth.sendConfirmEmail(userName).subscribe({
      next: (res) => {
        this._toastrService.success('Se ha enviado un nuevo código de verificación a su correo electrónico.');
        this.startCountdown();
        this.step.set(2); // Avanzar al paso de ingresar el código
      },
      error: (error) => {
        this._toastrService.error('Error al enviar el código de verificación.');
        this.canResend.set(true);
        this.countdown.set(0);
      }
    });
  }
  
  verifyEmail(): void {
    if (this.verificationForm.invalid) {
      this._toastrService.error('Por favor, ingrese un código de verificación válido.');
      return;
    }
    
    const userName = this.auth.getUserEmail();
    if (!userName) {
      this._toastrService.error('No se pudo obtener la información del usuario.');
      return;
    }
    
    const code = this.verificationForm.get('verificationCode')?.value;
    
    this.auth.confirmEmail({
      userName: userName,
      token: code
    }).subscribe({
      next: (res) => {
        if (res?.errors?.length > 0) {
          this._toastrService.error(res.errors[0]);
          return;
        }
        
        this._toastrService.success('Su correo electrónico ha sido verificado correctamente.');
        
        // Cerrar sesión para que vuelva a iniciar sesión con el correo ya verificado
        this.auth.logout('/login');
      },
      error: (error) => {
        this._toastrService.error('El código de verificación es inválido o ha expirado.');
      }
    });
  }

  private startCountdown(): void {
    // Cancelar cualquier timer existente
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }

    // Iniciar un nuevo timer
    this.timerSubscription = interval(1000)
      .pipe(
        takeWhile(() => this.countdown() > 0)
      )
      .subscribe({
        next: () => {
          this.countdown.update(current => current - 1);
          if (this.countdown() === 0) {
            this.canResend.set(true);
            if (this.timerSubscription) {
              this.timerSubscription.unsubscribe();
              this.timerSubscription = null;
            }
          }
        }
      });
  }

  ngOnDestroy(): void {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
  }
}
